import * as THREE from "three";
import { fbmDirection, ridgedDirection } from "./noise";
import type { PlanetDefinition } from "./types";

export interface TerrainSample {
  height: number;
  moisture: number;
  temperature: number;
  color: THREE.Color;
  biome: "ocean" | "beach" | "lowland" | "highland" | "rock" | "snow";
}

function smoothstep(edge0: number, edge1: number, value: number): number {
  const t = THREE.MathUtils.clamp((value - edge0) / Math.max(1e-9, edge1 - edge0), 0, 1);
  return t * t * (3 - 2 * t);
}

export function terrainHeight(direction: THREE.Vector3, planet: PlanetDefinition): number {
  const continent = fbmDirection(direction, planet.seed + 31, 5);
  const warped = new THREE.Vector3(
    direction.x + (fbmDirection(direction, planet.seed + 73, 3) - 0.5) * 0.21,
    direction.y + (fbmDirection(direction, planet.seed + 97, 3) - 0.5) * 0.21,
    direction.z + (fbmDirection(direction, planet.seed + 131, 3) - 0.5) * 0.21,
  ).normalize();
  const hills = fbmDirection(warped, planet.seed + 211, 6) * 2 - 1;
  const mountainRegion = smoothstep(0.55, 0.82, fbmDirection(warped, planet.seed + 271, 4));
  const ridges = ridgedDirection(warped, planet.seed + 331, 5);
  const continental = (continent - 0.48) * planet.terrainAmplitudeMeters * 2.2;
  const lowlands = hills * planet.terrainAmplitudeMeters * 0.55;
  const mountains = mountainRegion * Math.pow(ridges, 1.75) * planet.ridgeAmplitudeMeters;
  const erosion = fbmDirection(warped, planet.seed + 419, 4);
  return continental + lowlands + mountains * (0.45 + erosion * 0.55) - 70;
}

export function sampleTerrain(direction: THREE.Vector3, planet: PlanetDefinition): TerrainSample {
  const height = terrainHeight(direction, planet);
  const moisture = fbmDirection(direction, planet.seed + 503, 4);
  const temperature = THREE.MathUtils.clamp(1 - Math.abs(direction.y) * 0.72 + (fbmDirection(direction, planet.seed + 577, 3) - 0.5) * 0.25, 0, 1);
  const palette = planet.palette;
  let biome: TerrainSample["biome"];
  let color: string;
  if (height < planet.seaLevelMeters - 55) {
    biome = "ocean";
    color = palette.oceanDeep;
  } else if (height < planet.seaLevelMeters) {
    biome = "ocean";
    color = palette.oceanShallow;
  } else if (height < planet.seaLevelMeters + 16) {
    biome = "beach";
    color = palette.beach;
  } else if (height > 420 || (height > 300 && temperature < 0.43)) {
    biome = "snow";
    color = palette.snow;
  } else if (height > 250) {
    biome = "rock";
    color = palette.rock;
  } else if (height > 135) {
    biome = "highland";
    color = palette.highland;
  } else {
    biome = "lowland";
    color = palette.lowland;
  }
  const result = new THREE.Color(color);
  result.offsetHSL((moisture - 0.5) * 0.035, (moisture - 0.5) * 0.08, (temperature - 0.5) * 0.045);
  return { height, moisture, temperature, color: result, biome };
}

export function makeTangentFrame(normalInput: THREE.Vector3, planet: PlanetDefinition) {
  const normal = normalInput.clone().normalize();
  const reference = Math.abs(normal.y) > 0.92 ? new THREE.Vector3(1, 0, 0) : new THREE.Vector3(0, 1, 0);
  const east = new THREE.Vector3().crossVectors(reference, normal).normalize();
  const north = new THREE.Vector3().crossVectors(normal, east).normalize();
  const anchorHeight = terrainHeight(normal, planet);
  const anchorPoint = normal.clone().multiplyScalar(planet.diameterMeters * 0.5 + anchorHeight);
  return { normal, east, north, anchorPoint, anchorHeight };
}
